import { FC, useMemo, useState } from "react";
import { Modal } from "../modals";
import { VariationTypeEnum } from "@utilities/enums";
import { buildVariationsByType, isBothVariation, isColorVariation, isSizeVariation } from "@utilities/functions";
import { Product } from "@utilities/types";

interface ModalSelectVariationProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  onValidate: (selectedColor: string | null, selectedSize: string | null) => void;
}

export const ModalSelectVariation: FC<ModalSelectVariationProps> = ({
  isOpen,
  onClose,
  product,
  onValidate,
}) => {
  
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const { variationType, variations } = product;


  const formattedVariations = useMemo(() => buildVariationsByType(variationType, variations), [variationType, variations])
  const hasColors = useMemo(
    () => variationType === VariationTypeEnum.COLOR || variationType === VariationTypeEnum.BOTH,
    [variationType]
  );

  const hasSizes = useMemo(
    () => variationType === VariationTypeEnum.SIZE || variationType === VariationTypeEnum.BOTH,
    [variationType]
  );

  const availableColors = useMemo(() => {
    if (!formattedVariations) return [];

    if (variationType === VariationTypeEnum.BOTH && isBothVariation(formattedVariations)) {
      return formattedVariations.colorSizeData.map(cs => cs.color);
    }
    if (variationType === VariationTypeEnum.COLOR && isColorVariation(formattedVariations)) {
      return formattedVariations.colors.map(c => c.color);
    }

    return [];
  }, [variationType, formattedVariations]);

  const availableSizes = useMemo(() => {
    if (!formattedVariations) return [];

    if (variationType === VariationTypeEnum.BOTH && isBothVariation(formattedVariations)) {
      const colorEntry = formattedVariations.colorSizeData.find(cs => cs.color === selectedColor);
      return colorEntry?.sizes || [];
    }

    if (variationType === VariationTypeEnum.SIZE && isSizeVariation(formattedVariations)) {
      return formattedVariations.sizes;
    }

    return [];
  }, [variationType, formattedVariations, selectedColor]);

  const isValid = useMemo(() => {
    if (hasColors && !selectedColor) return false;
    if (hasSizes && !selectedSize) return false;
    return true;
  }, [hasColors, hasSizes, selectedColor, selectedSize]);

  const handleValidate = () => {
    onValidate(selectedColor, selectedSize);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sélectionnez votre variation">
      <div className="space-y-6">
        {hasColors && (
          <div>
            <h3 className="font-semibold mb-2">Couleurs disponibles</h3>
            <div className="flex gap-2 flex-wrap">
              {Array.isArray(availableColors) && availableColors.map((color, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedColor(color);
                    setSelectedSize(null); // reset taille quand on change de couleur
                  }}
                  className={`w-10 h-10 rounded-full border ${selectedColor === color ? "ring-2 ring-blue-500" : ""
                    }`}
                  style={{ backgroundColor: color, borderColor: "black" }}
                />
              ))}
            </div>
          </div>
        )}

        {hasSizes && (
          <div>
            <h3 className="font-semibold mb-2">Tailles disponibles</h3>
            <div className="flex gap-2 flex-wrap">
              {Array.isArray(availableSizes) && availableSizes.map((size, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedSize(size.size)}
                  className={`px-4 py-2 border rounded-md ${selectedSize === size.size ? "bg-blue-500 text-white" : "bg-white text-black hover:bg-gray-100"
                    }`}
                >
                  {size.size}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleValidate}
            disabled={!isValid}
            className={`btn ${!isValid ? "btn-disabled" : "btn-primary"}`}
          >
            Valider
          </button>
        </div>
      </div>
    </Modal>
  );
};
